import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import httpClient, { ApiResponse } from '../httpClient';
import { Transaction, TransactionCreateInput } from '../../models/transaction';
import { API_ENDPOINTS } from '../endpoints';
import { PaginatedResponse } from '../types';

export interface TransactionFilters {
    customer_id?: string;
    type?: string;
    status?: string;
    trading_account_id?: string;
    from_date?: string;
    to_date?: string;
    search?: string;
    payment_method?: string;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
    page?: number;
    per_page?: number;
}

// List all transactions (CRM)
const fetchAllTransactions = async (filters: TransactionFilters = {}) => {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            params.append(key, value.toString());
        }
    });

    const { data } = await httpClient.get<PaginatedResponse<Transaction>>(
        `${API_ENDPOINTS.CRM.TRANSACTIONS.LIST}?${params.toString()}`
    );
    return data;
};

export const useAllTransactions = (filters: TransactionFilters = {}) => {
    return useQuery<PaginatedResponse<Transaction>, Error>({
        queryKey: [API_ENDPOINTS.CRM.TRANSACTIONS.LIST, filters],
        queryFn: () => fetchAllTransactions(filters),
    });
};

// Get transaction by ID (CRM)
const fetchTransaction = async (id: string) => {
    const { data } = await httpClient.get<ApiResponse<Transaction>>(
        API_ENDPOINTS.CRM.TRANSACTIONS.DETAILS(id)
    );
    return data.data;
};

export const useTransaction = (id: string) => {
    return useQuery<Transaction, Error>({
        queryKey: [API_ENDPOINTS.CRM.TRANSACTIONS.LIST, id],
        queryFn: () => fetchTransaction(id),
        enabled: !!id,
    });
};

// Create transaction (CRM)
const createTransaction = async (input: TransactionCreateInput) => {
    const { data } = await httpClient.post<ApiResponse<Transaction>>(
        API_ENDPOINTS.CRM.TRANSACTIONS.CREATE,
        input
    );
    return data.data;
};

export const useCreateTransaction = () => {
    const queryClient = useQueryClient();

    return useMutation<Transaction, Error, TransactionCreateInput>({
        mutationFn: createTransaction,
        onSuccess: data => {
            // Invalidate general transactions list
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.CRM.TRANSACTIONS.LIST] });

            // Invalidate customer-specific transactions if customer_id is provided
            if (data.customer_id) {
                queryClient.invalidateQueries({
                    queryKey: ['customer-transactions', data.customer_id],
                });
                // Also invalidate trading accounts since transactions affect balances
                queryClient.invalidateQueries({
                    queryKey: ['customer-accounts', data.customer_id],
                });
            }

            // Invalidate all balance-related queries
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.TRADER.BALANCE.ME] });
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.TRADER.BALANCE.DETAILS] });
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.TRADER.BALANCE.WALLETS] });
            queryClient.invalidateQueries({
                queryKey: [API_ENDPOINTS.TRADER.BALANCE.TRADING_ACCOUNTS],
            });
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.TRADER.BALANCE.HISTORY] });
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.TRADER.BALANCE.PNL] });
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.TRADER.BALANCE.MARGIN] });
        },
    });
};

// Update transaction (CRM)
const updateTransaction = async ({
    id,
    ...data
}: { id: string } & Partial<TransactionCreateInput>) => {
    const { data: response } = await httpClient.put<ApiResponse<Transaction>>(
        API_ENDPOINTS.CRM.TRANSACTIONS.UPDATE(id),
        data
    );
    return response.data;
};

export const useUpdateTransaction = () => {
    const queryClient = useQueryClient();

    return useMutation<Transaction, Error, { id: string } & Partial<TransactionCreateInput>>({
        mutationFn: updateTransaction,
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.CRM.TRANSACTIONS.LIST] });
            queryClient.invalidateQueries({
                queryKey: [API_ENDPOINTS.CRM.TRANSACTIONS.LIST, variables.id],
            });

            // Invalidate customer-specific transactions if customer_id is provided
            if (data.customer_id) {
                queryClient.invalidateQueries({
                    queryKey: ['customer-transactions', data.customer_id],
                });
                // Also invalidate trading accounts since transactions affect balances
                queryClient.invalidateQueries({
                    queryKey: ['customer-accounts', data.customer_id],
                });
            }

            // Invalidate all balance-related queries
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.TRADER.BALANCE.ME] });
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.TRADER.BALANCE.DETAILS] });
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.TRADER.BALANCE.WALLETS] });
            queryClient.invalidateQueries({
                queryKey: [API_ENDPOINTS.TRADER.BALANCE.TRADING_ACCOUNTS],
            });
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.TRADER.BALANCE.HISTORY] });
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.TRADER.BALANCE.PNL] });
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.TRADER.BALANCE.MARGIN] });
        },
    });
};

// Delete transaction (CRM)
const deleteTransaction = async (id: string) => {
    await httpClient.delete(API_ENDPOINTS.CRM.TRANSACTIONS.DELETE(id));
};

export const useDeleteTransaction = () => {
    const queryClient = useQueryClient();

    return useMutation<void, Error, string>({
        mutationFn: deleteTransaction,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.CRM.TRANSACTIONS.LIST] });

            // Invalidate all customer-specific transactions since we don't have customer_id in delete
            queryClient.invalidateQueries({ queryKey: ['customer-transactions'] });
            // Also invalidate all trading accounts since transactions affect balances
            queryClient.invalidateQueries({ queryKey: ['customer-accounts'] });
        },
    });
};
